import type {
  BudgetCategory,
  ProjectHealth,
  ProjectRole,
  ProjectStatus,
} from "../../shared/types/api";
import type { Tone } from "../../shared/ui";

/**
 * How project vocabulary is shown.
 *
 * <p>Kept in one place so a status reads identically on the list, the detail page and a baseline
 * panel. Two screens disagreeing about what "on hold" looks like is the kind of small
 * inconsistency that makes an application feel unfinished.
 */

export const STATUS_LABELS: Record<ProjectStatus, string> = {
  DRAFT: "Draft",
  PLANNING: "Planning",
  PENDING_APPROVAL: "Pending approval",
  APPROVED: "Approved",
  ACTIVE: "Active",
  ON_HOLD: "On hold",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
  ARCHIVED: "Archived",
};

export const STATUS_TONE: Record<ProjectStatus, Tone> = {
  DRAFT: "gray",
  PLANNING: "blue",
  PENDING_APPROVAL: "amber",
  APPROVED: "blue",
  ACTIVE: "green",
  ON_HOLD: "amber",
  COMPLETED: "green",
  CANCELLED: "red",
  ARCHIVED: "gray",
};

/** Health is the one place colour carries meaning rather than decoration (§30). */
export const HEALTH_TONE: Record<ProjectHealth, Tone> = {
  GREEN: "green",
  AMBER: "amber",
  RED: "red",
};

export const CATEGORY_LABELS: Record<BudgetCategory, string> = {
  DEVELOPMENT: "Development",
  QA: "QA",
  DESIGN: "Design",
  BUSINESS_ANALYSIS: "Business analysis",
  DEVOPS: "DevOps",
  MANAGEMENT: "Management",
  SUPPORT: "Support",
  OTHER: "Other",
};

/** Ordered as §22 lists them, so the budget always reads in the same sequence. */
export const CATEGORY_ORDER: BudgetCategory[] = [
  "DEVELOPMENT",
  "QA",
  "DESIGN",
  "BUSINESS_ANALYSIS",
  "DEVOPS",
  "MANAGEMENT",
  "SUPPORT",
  "OTHER",
];

export const PROJECT_ROLE_LABELS: Record<ProjectRole, string> = {
  PROJECT_MANAGER: "Project manager",
  TEAM_LEAD: "Team lead",
  DEVELOPER: "Developer",
  QA: "QA",
  DESIGNER: "Designer",
  DEVOPS: "DevOps",
  BA: "Business analyst",
  STAKEHOLDER: "Stakeholder",
};

/**
 * Hours for display.
 *
 * <p>The server stores minutes so every sum stays exact; these are read, not summed, so a single
 * decimal is enough and thousands separators make four-figure budgets legible at a glance.
 */
export function formatHours(hours: number): string {
  const rounded = Math.round(hours * 10) / 10;
  return `${rounded.toLocaleString(undefined, { maximumFractionDigits: 1 })}h`;
}

/**
 * A variance figure with its sign.
 *
 * <p>The sign is the information. "450h" could be under or over; "+450h" cannot be misread, and
 * on a management report that difference matters more than the tidier-looking version.
 */
export function formatVariance(hours: number): string {
  const sign = hours > 0 ? "+" : "";
  return `${sign}${formatHours(hours)}`;
}

export function formatPercent(value: number): string {
  const sign = value > 0 ? "+" : "";
  return `${sign}${(Math.round(value * 100) / 100).toFixed(2)}%`;
}

/** Days late or early, phrased so nobody has to work out which direction is which. */
export function formatSlippage(days: number): string {
  if (days === 0) {
    return "on schedule";
  }
  return days > 0 ? `${days} days late` : `${Math.abs(days)} days early`;
}
